import Ionicons from "@expo/vector-icons/Ionicons";
import { userIdForHandle } from "@/lib/profile-handle";
import { useLocalSearchParams, useRouter } from "expo-router";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { StatusBar } from "expo-status-bar";
import { SafeAreaView } from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";

import { usersApi } from "@/api";
import { Avatar } from "@/components/ui/avatar";
import { notify } from "@/lib/dialog";
import i18n from "@/i18n";
import {
  friendshipGoalLabel,
  genderLabel,
  interestLabel,
  languageLabel,
  politicsLabel,
  profileListText,
  religionLabel,
} from "@/lib/profile-labels";
import { useAppTheme } from "@/theme/app-theme";
import type { GuestProfile } from "@/types";

function getProfileColors(isDark: boolean) {
  return {
    screen: isDark ? "#0D1724" : "#FCFDFF",
    topBar: isDark ? "#101C2C" : "#FCFDFF",
    surface: isDark ? "#152235" : "#FFFFFF",
    surfaceMuted: isDark ? "#19283B" : "#FAFBFD",
    surfaceSoft: isDark ? "#1B2C43" : "#F4F8FF",
    textPrimary: isDark ? "#EFF5FF" : "#102D51",
    textStrong: isDark ? "#DFEAFA" : "#153252",
    textBody: isDark ? "#C0CDDD" : "#425873",
    textSecondary: isDark ? "#A8B6C9" : "#7B879A",
    textMuted: isDark ? "#8999AD" : "#929DAC",
    border: isDark ? "#2C3C51" : "#E1E8F1",
    primary: isDark ? "#6EA8FF" : "#2468D8",
    primaryStrong: isDark ? "#91BCFF" : "#2463CB",
    primaryText: isDark ? "#B9D3FF" : "#3568B7",
    primarySoft: isDark ? "#203754" : "#F0F5FD",
    iconNeutral: isDark ? "#A9B9CD" : "#526C8D",
    success: isDark ? "#57C98D" : "#35A86C",
    successSoft: isDark ? "#19392E" : "#F1FAF5",
    shadow: isDark ? "#000000" : "#7187A6",
    white: "#FFFFFF",
  };
}

type ProfileColors = ReturnType<typeof getProfileColors>;

function listText(
  items?: (string | null | undefined)[] | null,
  limit?: number,
) {
  const values = items?.filter(Boolean) as string[] | undefined;
  if (!values?.length) return "";
  return values.slice(0, limit).join(" · ");
}

function valueText(value: unknown) {
  if (Array.isArray(value)) return listText(value);
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value.trim();
  return "";
}

export default function GuestProfileScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  // `v`, a per-tab handle, NOT the user id — see `lib/profile-handle.ts`. The
  // id used to travel in the URL, which put somebody else's identifier in the
  // address bar, the history, the `Referer`, and (per `public/_headers`) in
  // CSP reports the browser sends straight to Sentry, outside every redaction
  // layer we have.
  //
  // Resolving to null is a real state, not an error: a link opened in another
  // browser, a tab whose storage was cleared, or a reload where `sessionStorage`
  // was unavailable. It means "this tab never minted that handle", which is
  // exactly the case where refusing is right.
  const { v } = useLocalSearchParams<{ v?: string }>();
  const id = useMemo(() => userIdForHandle(v), [v]);
  // A handle that this tab never minted is a different failure from a person
  // who has gone: the first is a reloaded or shared link, the second is a
  // refusal from the server. Saying "they may no longer be active" about a
  // link that simply cannot resolve here sends the reader looking for a
  // problem that is not theirs.
  const unresolvedLink = !id;
  const { isDark } = useAppTheme();
  const colors = useMemo(() => getProfileColors(isDark), [isDark]);
  const styles = useMemo(() => createStyles(colors), [colors]);
  // What is on screen is keyed to the id it was FETCHED for, not to a separate
  // "loading" flag, because this screen is REUSED between people.
  //
  // `guest-profile` is a `Tabs.Screen` (see `(tabs)/_layout.tsx`), and
  // expo-router downgrades a push into a non-stack navigator to a NAVIGATE —
  // `global-state/getNavigationAction.js`: `if (type === 'PUSH' &&
  // navigationState.type !== 'stack') type = 'NAVIGATE'`. TabRouter's NAVIGATE
  // then finds the existing route by name, keeps its key (there is no `getId`
  // on this screen) and swaps only the params. So every profile after the
  // first lands on the SAME mounted instance, still holding the previous
  // person's state — the failure `tests/mobile/chat-screen-remount.test.ts`
  // describes for conversations, which this screen reaches by a different road.
  //
  // Clearing state inside the effect would not be enough: React renders before
  // it runs effects, so there would still be one frame of the previous
  // person's name, avatar, age band and interests under a screen opened for
  // somebody else. Carrying the id alongside the result makes that frame
  // unrepresentable rather than merely brief.
  const [result, setResult] = useState<{
    for: string;
    profile: GuestProfile | null;
  } | null>(null);
  // A result for anyone but the person now being asked for is not a result.
  // This is also what makes an unresolvable handle honest: `id` is null, so
  // nothing matches, and the empty state below is reached with nobody's
  // details still on screen.
  const settled = result && result.for === id ? result : null;
  const profile = settled?.profile ?? null;
  const loading = Boolean(id) && !settled;

  useEffect(() => {
    if (!id) return;
    let mounted = true;

    async function load(subjectId: string) {
      try {
        const next = await usersApi.guestProfile(subjectId);
        if (mounted) setResult({ for: subjectId, profile: next });
      } catch (err) {
        notify(
          t("profile.guest.unavailableTitle"),
          err instanceof Error ? err.message : t("errors.tryAgainInAMoment"),
        );
        // A refusal is a settled answer about THIS person, not a blank slate:
        // recording it against the id is what stops the spinner without
        // borrowing the last person's profile to fill the screen.
        if (mounted) setResult({ for: subjectId, profile: null });
      }
    }

    void load(id);

    return () => {
      mounted = false;
    };
  }, [id, t]);

  // The FULL list, not a "top 4". Interests are public — the nearby card and
  // the chat header already show them all — so this row is the one place they
  // live on this screen, and truncating it here would make the public profile
  // the least informative of the three surfaces. Bounded by the edit screen's
  // own cap on how many can be picked, not by a display limit.
  const interests = profileListText(profile?.interests, interestLabel, {
    separator: ", ",
  });
  const goals = profileListText(profile?.lookingFor, friendshipGoalLabel);
  const languages = profileListText(profile?.languages, languageLabel);
  const reveal = profile?.revealDetails ?? {};
  const revealGender =
    typeof reveal.gender === "string" ? genderLabel(reveal.gender) : "";
  const revealReligion =
    typeof reveal.religion === "string" ? religionLabel(reveal.religion) : "";
  const revealPolitics =
    typeof reveal.politics === "string" ? politicsLabel(reveal.politics) : "";

  return (
    <SafeAreaView style={styles.safe} edges={["top"]}>
      <StatusBar style={isDark ? "light" : "dark"} />

      <View style={styles.topBar}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t("profile.guest.backA11y")}
          hitSlop={10}
          onPress={() => router.back()}
          style={styles.backButton}
        >
          <Ionicons name="chevron-back" size={25} color={colors.textPrimary} />
        </Pressable>

        <Text style={styles.topBarTitle}>{t("profile.guest.kicker")}</Text>

        <View style={styles.backButton} />
      </View>

      {loading ? (
        <View style={styles.loading}>
          <ActivityIndicator color={colors.primary} />
        </View>
      ) : !profile ? (
        <View style={styles.empty}>
          <Ionicons
            name="person-circle-outline"
            size={42}
            color={colors.primary}
          />
          <Text style={styles.emptyTitle}>
            {t("profile.guest.unavailableTitle")}
          </Text>
          <Text style={styles.emptyText}>
            {t(
              unresolvedLink
                ? "profile.guest.unavailableLink"
                : "profile.guest.unavailableBody",
            )}
          </Text>
        </View>
      ) : (
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.content}
        >
          <View style={styles.heroCard}>
            <View style={styles.avatarWrap}>
              <Avatar
                seed={profile.avatarSeed}
                avatarConfig={profile.avatarConfig}
                name={profile.displayName}
                size={96}
                accessibilityLabel={t("profile.avatar.otherLabel", {
                  name: profile.displayName,
                })}
              />
            </View>

            <View style={styles.nameRow}>
              <Text style={styles.name} numberOfLines={2}>
                {profile.displayName}
                {profile.venueTag ? ` · ${profile.venueTag}` : ""}
              </Text>
              {/* The same mark every other surface puts beside this name — the
                  one place it was missing was the screen for looking somebody
                  up. Sized to the name it follows, like everywhere else. */}
            </View>

            {profile.ageBand ? (
              <Text style={styles.ageBand}>{profile.ageBand}</Text>
            ) : null}
          </View>

          <ProfileSection
            title={t("profile.guest.publicProfile")}
            icon="document-text-outline"
            styles={styles}
            colors={colors}
          >
            {/* Every row renders, answered or not — the same rule the reveal
                section below has always followed. A row that vanishes when
                empty is indistinguishable from a row that failed to load, and
                the section's shape changing from person to person makes the
                sparse profiles read as broken rather than as sparse.

                The headline moved here from the hero card 2026-08-20: floating
                above the sections it was the one public detail with no label
                saying what it was — "Joing" under an age band read as a
                glitch, not a headline. */}
            <DetailRow
              icon="chatbubble-ellipses-outline"
              label={t("profile.sections.headline")}
              value={profile.headline ?? ""}
              styles={styles}
              colors={colors}
            />

            <DetailRow
              icon="sparkles-outline"
              label={t("profile.sections.interests")}
              value={interests}
              styles={styles}
              colors={colors}
            />

            <DetailRow
              icon="people-outline"
              label={t("profile.guest.friendshipGoal")}
              value={goals}
              styles={styles}
              colors={colors}
            />

            <DetailRow
              icon="language-outline"
              label={t("profile.guest.languages")}
              value={languages}
              styles={styles}
              colors={colors}
            />
          </ProfileSection>

          <ProfileSection
            title={t("profile.guest.revealed")}
            icon={
              profile.revealed ? "lock-open-outline" : "lock-closed-outline"
            }
            styles={styles}
            colors={colors}
          >
            <View
              style={[
                styles.revealStatus,
                profile.revealed && styles.revealStatusOpen,
              ]}
            >
              {profile.revealed ? null : (
                <Ionicons
                  name="eye-off-outline"
                  size={16}
                  color={colors.iconNeutral}
                />
              )}
              <Text
                style={[
                  styles.revealStatusText,
                  profile.revealed && styles.revealStatusTextOpen,
                ]}
              >
                {profile.revealed
                  ? t("profile.guest.bothRevealed")
                  : t("profile.guest.revealLocked")}
              </Text>
            </View>

            <DetailRow
              icon="calendar-number-outline"
              label={t("profile.guest.actualAge")}
              value={reveal.age}
              locked={!profile.revealed}
              styles={styles}
              colors={colors}
            />
            <DetailRow
              icon="reader-outline"
              label={t("profile.guest.about")}
              value={reveal.about}
              locked={!profile.revealed}
              styles={styles}
              colors={colors}
            />
            <DetailRow
              icon="person-circle-outline"
              label={t("profile.sections.gender")}
              value={revealGender}
              locked={!profile.revealed}
              styles={styles}
              colors={colors}
            />
            <DetailRow
              icon="book-outline"
              label={t("profile.guest.religion")}
              value={revealReligion}
              locked={!profile.revealed}
              styles={styles}
              colors={colors}
            />
            <DetailRow
              icon="megaphone-outline"
              label={t("profile.sections.politics")}
              value={revealPolitics}
              locked={!profile.revealed}
              styles={styles}
              colors={colors}
            />
          </ProfileSection>
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

function ProfileSection({
  title,
  icon,
  children,
  styles,
  colors,
}: {
  title: string;
  icon: keyof typeof Ionicons.glyphMap;
  children: ReactNode;
  styles: ReturnType<typeof createStyles>;
  colors: ProfileColors;
}) {
  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <View style={styles.sectionIcon}>
          <Ionicons name={icon} size={18} color={colors.primary} />
        </View>
        <Text style={styles.sectionTitle}>{title}</Text>
      </View>

      <View style={styles.sectionBody}>{children}</View>
    </View>
  );
}

/**
 * One row, both halves of the screen.
 *
 * The public half was a bare label-and-value list while the reveal half had
 * icon chips, so a single screen read as two designs stacked. Worse, the four
 * fields it shares with the owner's own profile — headline, interests, what
 * brings you here, languages — carried an icon there and none here, which is
 * the kind of drift that makes two views of the same data look like two
 * different features. The icons below are exactly the ones
 * `app/(tabs)/profile.tsx` uses for the same fields: one field, one mark,
 * whichever side of the profile you are standing on.
 */
function DetailRow({
  icon,
  label,
  value,
  locked = false,
  styles,
  colors,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  value: unknown;
  locked?: boolean;
  styles: ReturnType<typeof createStyles>;
  colors: ProfileColors;
}) {
  const text = valueText(value);

  return (
    <View style={styles.detailRow}>
      <View style={styles.detailIcon}>
        <Ionicons name={icon} size={17} color={colors.iconNeutral} />
      </View>

      <View style={styles.detailCopy}>
        <Text style={styles.detailLabel}>{label}</Text>

        {locked ? (
          <View style={styles.lockedValue}>
            <View style={styles.lockedBarWide} />
            <View style={styles.lockedBarShort} />
            <Text style={styles.lockedText}>
              {i18n.t("profile.guest.blurred")}
            </Text>
          </View>
        ) : text ? (
          <Text style={styles.detailValue}>{text}</Text>
        ) : (
          // The same words and the same muted style on both halves, so "they
          // did not fill this in" reads identically wherever it appears.
          <Text style={styles.unansweredText}>
            {i18n.t("profile.guest.notAnswered")}
          </Text>
        )}
      </View>
    </View>
  );
}

function createStyles(colors: ProfileColors) {
  return StyleSheet.create({
    safe: {
      flex: 1,
      backgroundColor: colors.screen,
    },
    topBar: {
      minHeight: 64,
      paddingHorizontal: 18,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
      backgroundColor: colors.topBar,
    },
    backButton: {
      width: 42,
      height: 42,
      alignItems: "center",
      justifyContent: "center",
    },
    topBarTitle: {
      color: colors.primary,
      fontSize: 13,
      lineHeight: 18,
      fontWeight: "800",
      letterSpacing: 1.1,
    },
    content: {
      width: "100%",
      maxWidth: 680,
      alignSelf: "center",
      paddingHorizontal: 22,
      paddingTop: 22,
      paddingBottom: 42,
      gap: 16,
    },
    loading: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
    },
    empty: {
      flex: 1,
      padding: 28,
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
    },
    emptyTitle: {
      color: colors.textPrimary,
      fontSize: 20,
      fontWeight: "800",
    },
    emptyText: {
      maxWidth: 260,
      color: colors.textSecondary,
      fontSize: 14,
      lineHeight: 20,
      textAlign: "center",
    },
    heroCard: {
      padding: 22,
      alignItems: "center",
      borderRadius: 22,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      shadowColor: colors.shadow,
      shadowOpacity: 0.08,
      shadowRadius: 18,
      shadowOffset: { width: 0, height: 8 },
      elevation: 3,
    },
    avatarWrap: {
      marginBottom: 14,
      padding: 5,
      borderRadius: 58,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceSoft,
    },
    nameRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      // The badge is a sibling, not part of the Text, so a two-line name keeps
      // the mark vertically centred beside it rather than orphaned on line one.
      maxWidth: "100%",
    },
    name: {
      color: colors.textPrimary,
      fontSize: 25,
      lineHeight: 31,
      fontWeight: "800",
      textAlign: "center",
      flexShrink: 1,
    },
    ageBand: {
      marginTop: 5,
      color: colors.primaryText,
      fontSize: 13,
      lineHeight: 18,
      fontWeight: "800",
    },
    section: {
      padding: 18,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      gap: 14,
    },
    sectionHeader: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
    },
    sectionIcon: {
      width: 34,
      height: 34,
      borderRadius: 12,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.primarySoft,
    },
    sectionTitle: {
      color: colors.textPrimary,
      fontSize: 16,
      lineHeight: 22,
      fontWeight: "800",
    },
    sectionBody: {
      gap: 12,
    },
    revealStatus: {
      minHeight: 42,
      paddingHorizontal: 12,
      paddingVertical: 10,
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      borderRadius: 14,
      backgroundColor: colors.surfaceMuted,
    },
    revealStatusOpen: {
      backgroundColor: colors.successSoft,
    },
    revealStatusText: {
      flex: 1,
      color: colors.textSecondary,
      fontSize: 13,
      lineHeight: 18,
      fontWeight: "700",
    },
    revealStatusTextOpen: {
      color: colors.success,
    },
    detailRow: {
      flexDirection: "row",
      gap: 11,
      paddingTop: 2,
    },
    detailIcon: {
      width: 34,
      height: 34,
      borderRadius: 12,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.surfaceMuted,
    },
    detailCopy: {
      flex: 1,
      gap: 4,
    },
    detailLabel: {
      color: colors.textStrong,
      fontSize: 14,
      lineHeight: 19,
      fontWeight: "800",
    },
    detailValue: {
      color: colors.textBody,
      fontSize: 14,
      lineHeight: 21,
    },
    lockedValue: {
      gap: 5,
      opacity: 0.72,
    },
    lockedBarWide: {
      width: "82%",
      height: 10,
      borderRadius: 5,
      backgroundColor: colors.surfaceMuted,
    },
    lockedBarShort: {
      width: "48%",
      height: 10,
      borderRadius: 5,
      backgroundColor: colors.surfaceMuted,
    },
    lockedText: {
      color: colors.textMuted,
      fontSize: 12,
      lineHeight: 16,
      fontWeight: "700",
    },
    unansweredText: {
      color: colors.textMuted,
      fontSize: 13,
      lineHeight: 18,
      fontWeight: "700",
    },
  });
}
